import React, {useState} from "react";
import {Button, Col, Row} from "reactstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCirclePlus} from "@fortawesome/free-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import AdvancedTable from "../../../../commonComponents/AdvancedTable";
import {useLoggedInApi} from "../../../../api";
import {usePaymentProjects} from "../hooks";
import {useQueryClient} from "react-query";
import {toast} from "react-toastify";
import LotCreateModal from "../components/LotCreateModal";
import ToggleStatusButton from "./ToggleStatusButton";

export default function Lots() {
    const navigate = useNavigate();
    const apiClient = useLoggedInApi();
    const queryClient = useQueryClient();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const {asDictionary: projectDict, isLoading: projectsIsLoading} = usePaymentProjects();

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const handleSubmit = async (lotData) => {
        try {
            const resource = apiClient.resource('payment/lots');
            await resource.create(lotData);

            // Refresh the table
            queryClient.invalidateQueries(['table', 'payment/lots']);

            // Show success message
            toast.success("Lot added successfully");

            return true;
        } catch (error) {
            console.error("Error adding lot:", error);
            toast.error("Failed to add lot. Please try again.");
            throw error;
        }
    };

    return (
        <>
            <Row>
                <Col>
                    <h3 className="page-header pb-2">Lot Management</h3>
                </Col>
            </Row>
            <Row className="mt-4">
                <Col>
                    Manage lots and their payment details. Track lot status, pricing, and payment schedules.
                </Col>
            </Row>

            <Row className="mt-5">
                <Col>
                    <Button color="info-outline" onClick={toggleModal}>
                        <FontAwesomeIcon icon={faCirclePlus} />{" "}
                        ADD A LOT
                    </Button>
                </Col>
            </Row>

            <AdvancedTable
                friendlyResourceName={"Lot"}
                apiResourceName={'payment/lots'}
                columnDefinition={[
                    {
                        name: "Actions",
                        cell: row => (
                            <ToggleStatusButton lotId={row.id}
                                isActive={row.is_active} />
                        ),
                        sortable: false
                    },
                    {
                        name: "Status",
                        selector: row => {
                            if (row.is_active) {
                                return <span className="text-success">Active</span>;
                            } else {
                                return <span className="text-danger">Inactive</span>;
                            }
                        },
                        sortable: true,
                        sortField: 'is_active',
                        shrink: 1
                    },
                    {
                        name: "Lot ID",
                        selector: row => row.lot_cn,
                        sortable: true,
                        sortField: 'lot_cn'
                    },
                    {
                        name: "Project",
                        selector: row => {
                            if (projectsIsLoading) {
                                return <span>Loading...</span>;
                            }
                            return projectDict[row.payment_project]?.name || "Unknown Project" + ` (${row.payment_project})`;
                        },
                        sortable: false,
                        grow: 2
                    },
                    {
                        name: "Phase",
                        selector: row => row.phase,
                        sortable: false,
                        grow: 1
                    },
                    {
                        name: "Block",
                        selector: row => row.block,
                        sortable: false,
                        grow: 1
                    },
                    {
                        name: "Lot",
                        selector: row => row.lot,
                        sortable: false,
                        grow: 1
                    },
                    {
                        name: "Created Date",
                        selector: row => new Date(row.created_at).toLocaleDateString(),
                        sortable: true,
                        sortField: 'created_at'
                    }
                ]}
            />

            {/* Add Lot Modal */}
            <LotCreateModal 
                isOpen={isModalOpen} 
                toggle={toggleModal} 
                onSubmit={handleSubmit} 
            />
        </>
    );
}
